<template>
    <div>
        <div v-if="savedCard">
            <p class="mt-2 mb-4 fs-3">{{ $l10n.__('Ajouter une carte bancaire') }}</p>
            <card background="gray-0" class="flex-row ai-center jc-space">
                <div class="flex-row ai-center">
                    <SaasIcon
                        :icon="cardSvg"
                        :width="24"
                        :height="16"
                        class="mr-1 secondary-blue"
                    />
                    <span class="block ml-2">
                        {{ $filters.creditCard(savedCard.last4, '*', 4) }}
                    </span>
                    <span class="mx-2">—</span>
                    <span>
                        {{ savedCard.name }}
                    </span>
                </div>
                <div>
                    <span>
                        {{ $l10n.__('Expire le %s', expireDate) }}
                    </span>
                </div>
            </card>
            <CompassButton
                :label="$t.__('pages.account.CartStripeForm.replace_credit_card')"
                type="tertiary"
                class="mt-2 mb-4"
                @click="replaceCardForm"
            />
        </div>
        <div v-if="!savedCard">
            <p class="mt-2 mb-4 fs-3">{{ $l10n.__('Ajouter une carte bancaire') }}</p>
            <div class="flex-row card-inputs">
                <!-- CARD NUMBER -->
                <el-form-item
                    ref="cardNumber"
                    :class="{ disabled: isBillingContactPayer }"
                    prop="cardNumber"
                    :label="$t.__('pages.account.CartStripeForm.card_number')"
                    :error="errors.get('cardNumber')"
                >
                    <div id="stripe-number-card" class="card-input"></div>
                </el-form-item>

                <!-- CARD EXPIRY -->
                <el-form-item
                    ref="cardExpiry"
                    :class="{ disabled: isBillingContactPayer }"
                    prop="cardExpiry"
                    :label="$t.__('common.labels.expire_date')"
                    :error="errors.get('cardExpiry')"
                >
                    <div id="stripe-expiration-card" class="card-input"></div>
                </el-form-item>

                <!-- CARD CVC -->
                <el-form-item
                    ref="cardCvc"
                    :class="{ disabled: isBillingContactPayer }"
                    prop="cardCvc"
                    :label="$t.__('pages.account.CartStripeForm.cvc_cvv')"
                    :error="errors.get('cardCvc')"
                >
                    <div id="stripe-cvc-card" class="card-input"></div>
                </el-form-item>
            </div>
            <!-- CARD NAME -->
            <el-form-item
                class="card-name"
                :label="$t.__('pages.account.CartStripeForm.name_on_card')"
                prop="cardName"
            >
                <div class="stripe-name-card"></div>
                <el-input v-model="cardName" :disabled="isBillingContactPayer"></el-input>
            </el-form-item>
            <p class="fs-2 gray-60 info-disclaimer">
                {{
                    $l10n.__(
                        'Nous conservons de manière sécurisée vos informations bancaires afin de procéder automatiquement au paiement de votre abonnement en fonction des modalités de paiement choisies.',
                    )
                }}
            </p>
        </div>
    </div>
</template>

<script>
import StripeSellsy from 'Core/commons/StripeSellsy';
import Errors from 'Core/commons/Errors';
import { goToUrl } from 'js/vue/helpers';
import { getErrorInput, getErrorLabel } from 'api/sellsyaccountmanager';
import Card from 'vcomponents/Card.vue';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

export default {
    components: {
        SaasIcon,
        Card,
    },
    props: {
        store: {
            type: String,
            required: false,
            default: 'client',
        },
        isPublic: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            savedCard: null,
            stripe: null,
            errors: new Errors(),
        };
    },
    computed: {
        card() {
            return this.$store.getters[`${this.store}Cart/card`];
        },
        context() {
            return this.$store.getters[`${this.store}Cart/context`];
        },
        corporationId() {
            return this.$store.getters[`${this.store}Cart/corporationId`];
        },
        cardName: {
            get() {
                return this.card.cardName;
            },
            set(value) {
                this.setCardName(value);
            },
        },
        expireDate() {
            return moment(`${this.savedCard.expMonth}/${this.savedCard.expYear}`, 'M/YYYY').format(
                'MM/YYYY',
            );
        },
        cardSvg() {
            const cardType = this.savedCard.brand.replace(' ', '').toLowerCase();
            switch (cardType) {
                case 'mastercard':
                    return 'cardMastercard';
                case 'american-express':
                    return 'cardAmericanExpress';
                case 'diners-club':
                    return 'cardDinersClub';
                case 'discover':
                    return 'cardDiscover';
                case 'jcb':
                    return 'cardJCB';
                case 'unionpay':
                    return 'cardUnionPay';
                case 'visa':
                    return 'cardVisa';
                default:
                    return 'svgCard';
            }
        },
        isBillingContactPayer() {
            return this.$store.getters[`${this.store}Cart/isBillingContactPayer`];
        },
        confirmationPagePath() {
            let path = '';
            switch (this.context) {
                case 'new':
                    path = 'sam.subscription.payment-confirmed';
                    break;
                case 'upsale':
                    path = 'sam.upsell.payment-confirmed';
                    break;
                case 'renew':
                case 'legacy_renew':
                    path = 'sam.renew.payment-confirmed';
                    break;
            }

            return path;
        },
        /**
         * Account with SAM creation (aka v3 or v4) doesn't need Payment intent
         * on Upsell and Renew
         * or New with Card Stripe id
         */
        paymentWithoutIntent() {
            return (
                this.$store.getters[`${this.store}Cart/isUpsell`] ||
                (this.$store.getters[`${this.store}Cart/isRenew`] &&
                    this.$store.getters[`${this.store}Cart/previousPricingVersion`] >= 3)
            );
        },
    },
    created() {
        if (!this.isPublic) {
            this.getCard();
        } else {
            this.replaceCardForm();
        }
        EventBus.on('stripe:create', this.create);
        EventBus.on('stripe:public', this.publicPayment);

        EventBus.on('stripe:error', (payload) => {
            if (!this.isBillingContactPayer) {
                const error = payload;
                this.errors.setOneError(error.type, error.message);
            }
        });
        EventBus.on('stripe:clear', (payload) => {
            const event = payload;
            this.resetCard(event.type, event.event.complete);
        });
    },
    unmounted() {
        EventBus.off('stripe:create', this.create);
        EventBus.off('stripe:public', this.publicPayment);
        EventBus.off('stripe:error');
        EventBus.off('stripe:clear');
    },

    methods: {
        setCardNumber(value) {
            this.$store.commit(`${this.store}Cart/setCardNumber`, value);
        },
        setCardExpiry(value) {
            this.$store.commit(`${this.store}Cart/setCardExpiry`, value);
        },
        setCardCvc(value) {
            this.$store.commit(`${this.store}Cart/setCardCvc`, value);
        },
        setCardName(value) {
            this.$store.commit(`${this.store}Cart/setCardName`, value);
        },
        setComputeLoading(value) {
            this.$store.commit(`${this.store}Cart/setComputeLoading`, value);
        },
        recordStripeToken(value) {
            return this.$store.dispatch(`${this.store}Cart/recordStripeToken`, value);
        },
        markAsPaid(value) {
            return this.$store.dispatch(`${this.store}Cart/markAsPaid`, value);
        },
        createPaymentIntent(value) {
            return this.$store.dispatch(`${this.store}Cart/createPaymentIntent`, value);
        },
        replaceCardForm() {
            this.savedCard = null;
            this.stripe = new StripeSellsy();
        },
        getCard() {
            this.$store
                .dispatch(`${this.store}Cart/getActiveCreditCard`, this.corporationId)
                .then((res) => {
                    this.savedCard = res;
                    if (this.context === 'new') {
                        this.stripe = new StripeSellsy();
                        this.stripe.setCard(this.savedCard.id);
                    }
                })
                .catch(() => {
                    this.stripe = new StripeSellsy();
                });
        },
        create() {
            if (this.paymentWithoutIntent) this.validateWithAccountCard();
            else this.createCardToken();
        },
        /**
         * Validate Cart for an Account with already Stripe sync
         */
        validateWithAccountCard() {
            this.setComputeLoading(true);
            this.errors.clearAll();

            if (_.isNil(this.savedCard) || (this.context === 'new' && !!this.savedCard)) {
                this.addCard().then(() => {
                    goToUrl(this.$router.resolve({ name: this.confirmationPagePath }).href);
                });
            } else {
                this.markAsPaid().then(() => {
                    this.setComputeLoading(false);
                    goToUrl(this.$router.resolve({ name: this.confirmationPagePath }).href);
                });
            }
        },
        /**
         * Clear the last saved string for stripe verificaty then submit the new input
         * type (string) : name of stripe input
         * complete (boolean) : false if field isn't valide
         */
        resetCard(type, complete) {
            this.errors.clear(type);
            this.$refs[type].resetField();
            switch (type) {
                case 'cardNumber':
                    this.setCardNumber(complete);
                    break;
                case 'cardExpiry':
                    this.setCardExpiry(complete);
                    break;
                case 'cardCvc':
                    this.setCardCvc(complete);
                    break;
            }
        },

        /**
         * Foreach input of stripe, enable/disable inputs if checkbox for external payer is checked or not
         * plus, if checked, clear the value of stripe inputs in form.
         * type (string) : name of stripe input
         * stripe (Object) : StripeSellsy
         **/
        setStripeInputFields(type, stripe) {
            const resetElement = stripe.Elements._elements.find(
                (stripeEvent) => stripeEvent._componentName === type,
            );
            resetElement.update({ disabled: this.isBillingContactPayer });
            if (this.isBillingContactPayer) {
                resetElement.clear();
            }
        },

        /**
         * Be triggered by component's parent
         * and clear all values of credit card for the form && stripe.
         *
         * value (boolean) : if external payer or not
         */
        resetCardInputs(value) {
            if (!_.isNil(this.savedCard)) return;
            Object.values(this.$refs).forEach((element) => {
                this.setStripeInputFields(element.prop, this.stripe);
                if (value) {
                    this.resetCard(element.prop, false);
                }
            });
        },

        /**
         * Validate Cart with an non-Stripe sync Account
         */
        async createCardToken() {
            this.setComputeLoading(true);
            this.errors.clearAll();
            /**
             * Create PaymentIntent
             */
            const intent = await this.createPaymentIntent();
            /**
             * Confirm Payment with card on Stripe
             */
            try {
                this.stripe
                    .confirmCardPayment(intent.clientSecret)
                    .then(() => {
                        /**
                         * Create card if necessary and mark Cart as paid
                         */
                        this.addCard().then(() => {
                            goToUrl(this.$router.resolve({ name: this.confirmationPagePath }).href);
                        });
                    })
                    .catch((err) => {
                        this.errors.setOneError(getErrorInput(err.error), getErrorLabel(err.error));
                        Push.error(getErrorLabel(err.error));
                        this.setComputeLoading(false);
                    });
            } catch (err) {
                console.log(err);
            }
        },
        addCard() {
            return new Promise(async (resolve, reject) => {
                if (!this.savedCard) {
                    /**
                     * No Card in Stripe ? Create one with new Token
                     */
                    try {
                        const token = await this.stripe.createToken({ name: this.cardName });

                        if (token.error) {
                            throw new Error(token.error);
                        }
                        /**
                         * Save Card token in Sellsy
                         */
                        await this.recordStripeToken({
                            corporation: this.corporationId,
                            token: token.token.id,
                        });
                    } catch (err) {
                        this.setComputeLoading(false);
                        this.errors.setOneError(getErrorInput(err.error), getErrorLabel(err.error));
                        Push.error(getErrorLabel(err.error));
                        reject(err);
                    }
                }
                /**
                 * Finally, just mark Cart as paid in Sellsy
                 */
                this.markAsPaid().then(() => {
                    this.setComputeLoading(false);
                    resolve();
                });
            });
        },
        async publicPayment() {
            this.setComputeLoading(true);
            this.errors.clearAll();
            /**
             * Create PaymentIntent
             */
            const intent = await this.createPaymentIntent();
            /**
             * Confirm Payment with card on Stripe
             */
            this.stripe
                .confirmCardPayment(intent.clientSecret)
                .then(() => {
                    this.markAsPaid().then(() => {
                        this.setComputeLoading(false);
                        EventBus.emit('stripe:public:paid');
                    });
                })
                .catch((error) => {
                    this.setComputeLoading(false);
                    Push.error(error);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;
.card-inputs {
    :deep(.el-form-item.is-error) {
        .card-input {
            border: 1px solid $alert-50 !important;
        }
    }
}
.el-checkbox .el-checkbox__input {
    vertical-align: top;
    padding-top: 4px;
}

.disabled {
    :deep(.el-form-item__content) {
        cursor: not-allowed !important;
    }
    .card-input,
    :deep(.el-input__inner) {
        color: $gray-60 !important;
        background-color: $gray-0 !important;
        border-color: $gray-05 !important;
        pointer-events: none !important;
    }
}

.card-name {
    :deep(.el-input) {
        .el-input__inner {
            height: 32px !important;
        }
    }
}

.info-disclaimer {
    line-height: 16px;
}

@media screen and (min-width: 620px) {
    .card-inputs {
        display: flex;
        flex-direction: row;

        :deep(.el-form-item) {
            &:first-child {
                width: 60%;
            }

            &:nth-child(2),
            &:last-child {
                width: 20%;
                margin-left: 24px;
            }
        }
    }
}
</style>
